import type { SavingsPointsData } from '../hooks/useGetSavingsPoints';
import { parseContactMonth } from './formatSavingsPoints';

export type SavingsPointsWidgetState =
  | '1'   // No finance requirements form
  | '2'   // Building points (default)
  | '2a'  // Below £5k minimum investment
  | '2c'  // Down payment ready, still building points
  | '2f'  // Fully divested
  | '3'   // Points done, down payment missing
  | '3a'  // Consortium investor: points met, no PFHPP/GYS Home balance
  | '4'   // Ready & in queue
  | '5'   // Near contact (within 3 months)
  | '6a'  // DIP in progress (LETTER_SENT)
  | '6b'; // DIP expired

const MIN_INVESTMENT = 5000;

function isContactWithinThreeMonths(contactMonth: string | null): boolean {
  if (!contactMonth) return false;
  const contact = parseContactMonth(contactMonth);
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
  return contact <= threeMonthsFromNow;
}

export function getSavingsPointsWidgetState(data: SavingsPointsData): SavingsPointsWidgetState {
  const totalInvestment = parseFloat(data.total_investment ?? '0');
  const pfhppInvestment = parseFloat(data.pfhpp_investment ?? '0');

  // Resolution order: 3a → 2f → 6a → 6b → 5 → 4 → 1 → 2a → 3 → 2c → 2
  if (data.has_enough_points && !data.has_down_payment_ready && pfhppInvestment <= 0 && totalInvestment > 0) return '3a';

  if (totalInvestment <= 0 && data.savings_points_earned !== null) return '2f';

  if (data.dip_status === 'LETTER_SENT') return '6a';

  if (data.dip_status === 'EXPIRED') return '6b';

  if (
    data.has_enough_points &&
    data.has_down_payment_ready &&
    isContactWithinThreeMonths(data.contact_month)
  )
    return '5';

  if (data.has_enough_points && data.has_down_payment_ready) return '4';

  if (!data.has_fr_form) return '1';

  if (totalInvestment < MIN_INVESTMENT) return '2a';

  if (data.has_enough_points && !data.has_down_payment_ready) return '3';

  if (data.has_down_payment_ready) return '2c';

  return '2';
}
